
import {TextSprite} from "../TextSprite.js";

// Standing visual style for every label created by the digitization module
// (and retrofitted onto the existing Midpoint measurement label) - this is
// the SINGLE place that controls it; every tool creates its labels via
// createStyledLabel() below, so they're identical by construction, not by
// convention. Dark charcoal background (matches the existing dark-UI
// convention already used by WallThicknessPrompt.js/RelaxedFitPrompt.js's
// modal background, #2b2b2b - reused here rather than inventing a new dark
// tone) with white text, for real contrast - the previous light-gray
// background (211,211,211) was too close to white text and to a typically
// bright point-cloud surface behind it, which is what made labels read as
// washed out/too light.
//
// Background alpha (user-reported, after LABEL_RENDER_ORDER below already
// put every label strictly on top of a Polygon's own fill / any feature
// geometry): drawing ON TOP is a render-ORDER question - it says nothing
// about whether the label's own background pixels stay OPAQUE once drawn.
// At the previous alpha (0.6 - 60% opaque), 40% of whatever sat behind a
// label (a bright polygon fill, a light point-cloud patch) blended straight
// through the background rectangle itself, so a label over e.g. a green
// polygon still read as a washed-out, greenish box - correctly drawn on
// top, but not visually reading as its own separate information layer. A
// high, near-opaque background alpha fixes this at its actual source (the
// background fill), independent of renderOrder. Shared by BOTH label
// variants (see LABEL_BACKGROUND below) - the ordinary-vs-result
// distinction is carried ENTIRELY by border colour, never by tinting the
// background differently.
const LABEL_BACKGROUND = {r: 43, g: 43, b: 43, a: 0.92};

// Border/outline is a fully opaque accent blue (#007FFF / rgb(0,127,255)) -
// outline thickness itself is TextSprite's own borderThickness (unchanged),
// only the color is a styling concern here. Result-label border colour
// (task: "resultat-label skal have orange border") - reuses the SAME
// orange already established elsewhere in this module for "this is a
// significant, computed/selected result" (FeatureVisual's COLOR_SELECTED /
// VectorEditingMenu's range-highlight family, 0xffa500-ish), rather than
// inventing a new accent. Background/text stay identical to a normal label
// (LABEL_BACKGROUND above) - only the border distinguishes a resultLabel
// (Linje/Polygon's "Samlet længde"/"Areal", PipeVisual's dimension/kote
// summary) from an ordinary coordinate/measurement label, so the two are
// visually distinct even when both happen to be visible at once.
const RESULT_BORDER = {r: 255, g: 165, b: 0, a: 1.0};

// Shared "always draw on top of a Polygon's own semi-transparent fill"
// renderOrder - user-reported: labels/markers were getting visually painted
// over by FeatureVisual._rebuildFill(), which (unlike every label/marker in
// this module) deliberately keeps depthTest/depthWrite ON (see that
// method's own doc comment for why). depthTest:false on a label/marker's
// OWN material only means it never gets REJECTED by whatever is already in
// the depth buffer - it says nothing about draw ORDER, so if the fill
// happened to be drawn AFTER a label/marker in THREE's distance-sorted
// transparent queue, its color still painted straight over pixels already
// drawn underneath. TextSprite.js's own sprite already sets this exact
// value (`this.sprite.renderOrder = 10`, established there for the same
// "Areal og volumen" TIN-surface bug) - reused here as the ONE canonical
// value for anything in Digitalisering that must never end up hidden
// behind a fill: CopyIconMarker.js's copy-icon glyphs, and
// FeatureVisual.js's vertex spheres/edges, all adopt it too, so every
// interactive marker - not just the label text itself - stays consistently
// on top regardless of camera angle/distance. The fill mesh's own
// renderOrder is left at THREE's default (0, unchanged - see
// _rebuildFill()), so this only ever needs to be "higher than 0", not
// coordinated further.
export const LABEL_RENDER_ORDER = 10;

// GCP-labels (Lag -> GCP'er): samme label som alle andre, kun med rød kant, så de kan kendes fra koordinat-
// (blå) og resultat-labels (orange).
const GCP_BORDER = {r: 229, g: 57, b: 53, a: 1.0};

export const DigitizationStyle = {
	label: {
		background: LABEL_BACKGROUND,
		border: {r: 0, g: 127, b: 255, a: 1.0},
		text: {r: 255, g: 255, b: 255, a: 1.0},
	},
	resultLabel: {
		background: LABEL_BACKGROUND,
		border: RESULT_BORDER,
		text: {r: 255, g: 255, b: 255, a: 1.0},
	},
	gcpLabel: {
		background: LABEL_BACKGROUND,
		border: GCP_BORDER,
		text: {r: 255, g: 255, b: 255, a: 1.0},
	},
};

// `variant`: "label" (default), "result" or "gcp" - see DigitizationStyle.resultLabel /
// gcpLabel above. Everything else about a label (size, depth-test-off, transparency,
// starting hidden) is identical between the two; only the border colour and
// which style object it's sourced from differ.
//
// No renderOrder set here: `label` is TextSprite's own OUTER Object3D
// wrapper (not itself renderable, THREE never reads its renderOrder) -
// the actual draw-order fix (renderOrder LABEL_RENDER_ORDER) already lives
// on `label.sprite`, the real THREE.Sprite TextSprite's own constructor
// creates and adds as a child - see TextSprite.js's own comment there.
export function createStyledLabel(text = "", variant = "label"){
	const style = variant === "result" ? DigitizationStyle.resultLabel
		: variant === "gcp" ? DigitizationStyle.gcpLabel : DigitizationStyle.label;
	const label = new TextSprite("");

	// kept small/restrained so labels don't dominate the 3D view (their
	// on-screen SIZE beyond that is controlled separately and kept constant
	// regardless of zoom - see ScreenSpaceScale.js's ScreenSpaceSizes.label)
	// Stil og skriftstørrelse sættes i ét hug (én canvas/tekstur) i stedet for fire tegninger pr. oprettet label.
	// Uændret adfærd: tekst givet ved oprettelsen tegnes én gang i TextSprites standardstørrelse (28), før skriftstørrelsen
	// sættes (se kommentaren i precisionMeasureTools.js); tom tekst + senere setText() tegnes i 15.
	label.configure({background: style.background, border: style.border, text: style.text, fontsize: text ? undefined : 15});
	if(text){
		label.setText(text);
		label.fontsize = 15;
	}
	label.material.depthTest = false;
	label.material.opacity = 1;
	// required for the semi-transparent background to actually blend instead
	// of being drawn opaque
	label.material.transparent = true;
	label.visible = false;

	return label;
}
